import { Link } from './SmartLink';
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { motion, AnimatePresence } from 'motion/react';
import {
  Menu, X, Phone, Mail, ChevronDown,
  Facebook, Instagram,
  FileText, MessageCircle, Briefcase,
} from 'lucide-react';
import { COMPANY } from '../lib/company';
import { CORPORATE_NAV } from '../data/corporateIndustries';

// --- Visa dropdown links -------------------------------------------------------
const VISA_LINKS = [
  { label: 'AE UAE Visit Visa',  path: '/visas/uae-visit-visa',  featured: true },
  { label: 'EU Schengen Visa',   path: '/visas/schengen-visa',   featured: true },
  { label: 'UK UK Visa',         path: '/visas/uk-visa' },
  { label: 'US USA Visa',        path: '/visas/usa-visa' },
  { label: 'CA Canada Visa',     path: '/visas/canada-visa' },
  { label: 'AU Australia Visa',  path: '/visas/australia-visa' },
  { label: 'SA Saudi Visa',      path: '/visas/saudi-visa',      featured: true },
  { label: 'AE UAE Golden Visa', path: '/visas/golden-visa',     featured: true },
  { label: 'AE Visa Extension',  path: '/visas/visa-extension' },
  { label: 'AE Family Visa',     path: '/visas/family-visa' },
  { label: 'AE Job Seeker Visa', path: '/visas/job-seeker-visa' },
  { label: 'AE Transit Visa',    path: '/visas/transit-visa' },
  { label: 'AE Freelance Visa',  path: '/visas/freelance-visa' },
];

const VISA_PATHS = ['/visas', '/visas/'];

const WA_URL = `https://wa.me/${COMPANY.whatsapp}`;

// --- Main Component ------------------------------------------------------------
export default function Navbar() {
  const [visaOpen, setVisaOpen]     = useState(false);
  const [isOpen, setIsOpen]         = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const [visaMobOpen, setVisaMobOpen] = useState(false);
  const [corpOpen, setCorpOpen]       = useState(false);
  const [corpMobOpen, setCorpMobOpen] = useState(false);
  const location = useLocation();
  const navRef   = useRef<HTMLElement>(null);
  const openTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerC  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerC = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    setVisaOpen(false); setIsOpen(false); setVisaMobOpen(false); setCorpOpen(false); setCorpMobOpen(false);
  }, [location]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { setVisaOpen(false); setCorpOpen(false); setIsOpen(false); } };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) { setVisaOpen(false); setCorpOpen(false); }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => () => {
    if (openTimer.current)  clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimerC.current)  clearTimeout(openTimerC.current);
    if (closeTimerC.current) clearTimeout(closeTimerC.current);
  }, []);

  const openVisa = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (openTimer.current)  { clearTimeout(openTimer.current);  openTimer.current  = null; }
    setCorpOpen(false);
    openTimer.current = setTimeout(() => { setVisaOpen(true); openTimer.current = null; }, 160);
  };

  const scheduleClose = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    closeTimer.current = setTimeout(() => { setVisaOpen(false); closeTimer.current = null; }, 260);
  };

  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };

  const openCorp = () => {
    if (closeTimerC.current) { clearTimeout(closeTimerC.current); closeTimerC.current = null; }
    if (openTimerC.current)  { clearTimeout(openTimerC.current);  openTimerC.current  = null; }
    setVisaOpen(false);
    openTimerC.current = setTimeout(() => { setCorpOpen(true); openTimerC.current = null; }, 160);
  };

  const scheduleCloseCorp = () => {
    if (openTimerC.current) { clearTimeout(openTimerC.current); openTimerC.current = null; }
    closeTimerC.current = setTimeout(() => { setCorpOpen(false); closeTimerC.current = null; }, 260);
  };

  const cancelCloseCorp = () => {
    if (closeTimerC.current) { clearTimeout(closeTimerC.current); closeTimerC.current = null; }
  };

  const isVisaActive = VISA_PATHS.some(p =>
    location.pathname === p || location.pathname.startsWith(p + '/')
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* -- Row 1: Top Utility Bar -- */}
      <div
        className="hidden md:flex items-center justify-between px-8 h-8 text-[11px]"
        style={{ background: '#071126', color: '#A8D8F0' }}
      >
        <div className="flex items-center gap-6">
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, '')}`}
            className="flex items-center gap-1.5 hover:text-white transition-colors font-medium"
          >
            <Phone className="w-3 h-3" /> {COMPANY.phone}
          </a>
          <a
            href={`mailto:${COMPANY.email}`}
            className="flex items-center gap-1.5 hover:text-white transition-colors font-medium"
          >
            <Mail className="w-3 h-3" /> {COMPANY.email}
          </a>
        </div>
        <div className="flex items-center gap-3">
          <a href={COMPANY.social.facebook}  target="_blank" rel="noopener noreferrer" aria-label="Facebook"  className="hover:text-white transition-colors"><Facebook  className="w-3 h-3" /></a>
          <a href={COMPANY.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white transition-colors"><Instagram className="w-3 h-3" /></a>
        </div>
      </div>

      {/* -- Row 2: Main Navbar -- */}
      <nav
        ref={navRef}
        className={`sticky top-0 z-[100] bg-white transition-all duration-300 ${
          scrolled
            ? 'shadow-[0_4px_24px_rgba(11,31,58,0.10)]'
            : 'shadow-[0_1px_0_rgba(11,31,58,0.05)]'
        }`}
        style={{ borderBottom: scrolled ? 'none' : '0.5px solid #EAEAEA' }}
      >
        <div className="max-w-[1360px] mx-auto px-6 lg:px-8 flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 group" aria-label="Big Bird Travel home">
            <img
              src="/logo.png"
              alt="Big Bird Travel and Tourism"
              className="h-14 w-auto max-w-[188px] object-contain transition-transform duration-200 group-hover:scale-[1.02] sm:h-16 lg:max-w-[210px]" loading="lazy" />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden xl:flex items-center h-full gap-0">

            <NavItem to="/" active={location.pathname === '/'}>Home</NavItem>

            {/* Visa Services dropdown trigger */}
            <NavTrigger
              active={isVisaActive}
              isMenuOpen={visaOpen}
              onMouseEnter={openVisa}
              onMouseLeave={scheduleClose}
              onClick={() => setVisaOpen(v => !v)}
            >
              Visa Services
            </NavTrigger>

            <NavItem to="/tour-packages"    active={isActive('/tour-packages')}>Holiday Packages</NavItem>
            <NavItem to="/uae-tours"        active={isActive('/uae-tours')}>UAE Tours</NavItem>
            <NavItem to="/flights"          active={isActive('/flights')}>Air Tickets</NavItem>
            <NavItem to="/hotels"           active={isActive('/hotels')}>Hotel Booking</NavItem>
            <NavItem to="/umrah"            active={isActive('/umrah')}>Umrah Packages</NavItem>

            {/* Corporate Travel dropdown trigger */}
            <NavTrigger
              active={isActive('/corporate-travel')}
              isMenuOpen={corpOpen}
              onMouseEnter={openCorp}
              onMouseLeave={scheduleCloseCorp}
              onClick={() => { setCorpOpen(v => !v); setVisaOpen(false); }}
            >
              Corporate Travel
            </NavTrigger>

            <NavItem to="/contact"          active={isActive('/contact')}>Contact</NavItem>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden xl:flex items-center gap-2 shrink-0">
            {/* WhatsApp CTA */}
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp - chat with Big Bird Travel"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg font-black text-white text-[11.5px] uppercase tracking-wider transition-all hover:opacity-90 hover:shadow-lg hover:shadow-[#25D366]/25"
              style={{ background: '#0F7A35' }}
            >
              <MessageCircle className="w-3.5 h-3.5 shrink-0" />
              WhatsApp
            </a>
            {/* Plan My Trip */}
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 font-display font-semibold text-[11.5px] uppercase tracking-[0.3px] text-white rounded-[8px] transition-all hover:shadow-lg hover:shadow-[#0096FF]/25 hover:scale-[1.02] active:scale-[0.99] whitespace-nowrap"
              style={{ background: '#0068B3', padding: '10px 14px' }}
            >
              Plan My Trip <span className="text-[14px] leading-none">→</span>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="xl:hidden p-2.5 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsOpen(true)}
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5 text-[#071126]" />
          </button>
        </div>

        {/* -- Visa Services Dropdown Panel -- */}
        <AnimatePresence>
          {visaOpen && (
            <motion.div
              key="visa-mega"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-full left-0 right-0 z-50 bg-white overflow-hidden"
              style={{ borderBottom: '1px solid #EAEAEA', boxShadow: '0 12px 32px rgba(11,31,58,0.09)' }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              role="region"
              aria-label="Visa services navigation"
            >
              {/* Brand accent line */}
              <div
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{ background: 'linear-gradient(to right, #0096FF 0%, #0096FF 20%, rgba(29,168,224,0) 100%)' }}
              />
              <div className="max-w-[1360px] mx-auto px-8 py-6">
                <VisaMegaPanel />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* -- Corporate Travel Dropdown Panel -- */}
        <AnimatePresence>
          {corpOpen && (
            <motion.div
              key="corp-mega"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-full left-0 right-0 z-50 bg-white overflow-hidden"
              style={{ borderBottom: '1px solid #EAEAEA', boxShadow: '0 12px 32px rgba(11,31,58,0.09)' }}
              onMouseEnter={cancelCloseCorp}
              onMouseLeave={scheduleCloseCorp}
              role="region"
              aria-label="Corporate travel navigation"
            >
              <div
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{ background: 'linear-gradient(to right, #0096FF 0%, #0096FF 20%, rgba(29,168,224,0) 100%)' }}
              />
              <div className="max-w-[1360px] mx-auto px-8 py-6">
                <CorporateMegaPanel active={location.pathname} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* -- Mobile Full-Screen Overlay -- */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="fixed inset-0 z-[200] bg-white flex flex-col"
          >
            {/* Mobile header */}
            <div
              className="flex items-center justify-between px-5 py-4 shrink-0"
              style={{ borderBottom: '1px solid #EAEAEA' }}
            >
              <Link to="/" className="flex items-center" onClick={() => setIsOpen(false)} aria-label="Big Bird Travel home">
                <img
                  src="/logo.png"
                  alt="Big Bird Travel and Tourism"
                  className="h-12 w-auto max-w-[160px] object-contain" loading="lazy" />
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                aria-label="Close navigation menu"
              >
                <X className="w-5 h-5 text-[#071126]" />
              </button>
            </div>

            {/* Scrollable nav items */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">

              <MobLink to="/" onClose={() => setIsOpen(false)}>Home</MobLink>

              {/* Visa Services accordion */}
              <div
                className="rounded-lg overflow-hidden"
                style={{ border: '0.5px solid #EAEAEA' }}
              >
                <button
                  onClick={() => setVisaMobOpen(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-3 font-display font-medium text-[13px] text-[#071126] uppercase tracking-[0.3px] hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 rounded-[7px] flex items-center justify-center shrink-0"
                      style={{ background: '#FAECE7' }}
                    >
                      <FileText className="w-3.5 h-3.5" style={{ color: '#993C1D', strokeWidth: 2 }} />
                    </div>
                    Visa Services
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-[#0068B3] transition-transform duration-200 ${visaMobOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {visaMobOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden bg-slate-50/60 px-3 pb-3"
                      style={{ borderTop: '0.5px solid #EAEAEA' }}
                    >
                      <div className="pt-2 space-y-0.5">
                        {VISA_LINKS.map(l => (
                          <Link
                            key={l.label}
                            to={l.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-2 py-2 px-2 rounded-lg text-[13px] font-sans transition-colors hover:bg-[#E8F4FD] hover:text-[#0068B3] ${
                              l.featured ? 'text-[#0068B3] font-medium' : 'text-[#3A3A50]'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${l.featured ? 'bg-[#0096FF]' : 'bg-gray-300'}`} />
                            {l.label}
                          </Link>
                        ))}
                        <Link
                          to="/visas"
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-1 pt-2 mt-1 border-t border-[#EAEAEA] text-[11px] uppercase tracking-[0.6px] text-[#0068B3] font-semibold font-display px-2"
                        >
                          All Visa Services →
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <MobLink to="/tour-packages"    onClose={() => setIsOpen(false)}>Holiday Packages</MobLink>
              <MobLink to="/uae-tours"        onClose={() => setIsOpen(false)}>UAE Tours</MobLink>
              <MobLink to="/flights"          onClose={() => setIsOpen(false)}>Air Tickets</MobLink>
              <MobLink to="/hotels"           onClose={() => setIsOpen(false)}>Hotel Booking</MobLink>
              <MobLink to="/umrah"            onClose={() => setIsOpen(false)}>Umrah Packages</MobLink>

              {/* Corporate Travel accordion */}
              <div className="rounded-lg overflow-hidden" style={{ border: '0.5px solid #EAEAEA' }}>
                <button
                  onClick={() => setCorpMobOpen(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-3 font-display font-medium text-[13px] text-[#071126] uppercase tracking-[0.3px] hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-[7px] flex items-center justify-center shrink-0" style={{ background: '#E8F4FD' }}>
                      <Briefcase className="w-3.5 h-3.5" style={{ color: '#0068B3', strokeWidth: 2 }} />
                    </div>
                    Corporate Travel
                  </span>
                  <ChevronDown className={`w-4 h-4 text-[#0068B3] transition-transform duration-200 ${corpMobOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {corpMobOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden bg-slate-50/60 px-3 pb-3"
                      style={{ borderTop: '0.5px solid #EAEAEA' }}
                    >
                      <div className="pt-2 space-y-0.5">
                        <Link
                          to="/corporate-travel"
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-2 py-2 px-2 rounded-lg text-[13px] font-sans font-medium text-[#0068B3] transition-colors hover:bg-[#E8F4FD]"
                        >
                          <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-[#0096FF]" />
                          Corporate Travel Overview
                        </Link>
                        {CORPORATE_NAV.map(l => (
                          <Link
                            key={l.path}
                            to={l.path}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-2 py-2 px-2 rounded-lg text-[13px] font-sans text-[#3A3A50] transition-colors hover:bg-[#E8F4FD] hover:text-[#0068B3]"
                          >
                            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-gray-300" />
                            {l.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <MobLink to="/contact"          onClose={() => setIsOpen(false)}>Contact Us</MobLink>

              {/* Plan My Trip CTA */}
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="mt-3 flex items-center justify-center gap-2 font-display font-semibold text-[13px] uppercase tracking-[0.4px] text-white rounded-[10px] py-3.5 transition-all hover:opacity-90"
                style={{ background: '#0096FF' }}
              >
                Plan My Trip →
              </Link>
            </div>

            {/* Sticky bottom - WhatsApp */}
            <div className="shrink-0 px-4 py-4" style={{ borderTop: '1px solid #EAEAEA' }}>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Us - chat with Big Bird Travel"
                className="flex items-center justify-center gap-2.5 text-white font-black text-[13px] uppercase tracking-wider py-3.5 rounded-[10px] transition-all hover:opacity-90 active:scale-[0.99]"
                style={{ background: '#0F7A35' }}
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// --- Visa Mega Panel -----------------------------------------------------------
function VisaMegaPanel() {
  return (
    <div className="flex gap-8">
      {/* Left: visa links in 2 columns */}
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-4 pb-3" style={{ borderBottom: '0.5px solid #EAEAEA' }}>
          <div
            className="w-8 h-8 rounded-[8px] flex items-center justify-center shrink-0"
            style={{ background: '#FAECE7' }}
          >
            <FileText className="w-4 h-4" style={{ color: '#993C1D', strokeWidth: 2 }} />
          </div>
          <div>
            <p className="font-display font-semibold text-[13px] text-[#071126] leading-tight">Visa Services</p>
            <p className="font-sans text-[11px] text-[#999] mt-0.5">45+ countries covered - document guidance & fast processing</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-0.5">
          {VISA_LINKS.map(l => (
            <Link
              key={l.label}
              to={l.path}
              className={`group flex items-center justify-between py-2.5 border-b border-[#F5F5F5] text-[13px] font-sans transition-all duration-150 hover:text-[#0068B3] hover:translate-x-[2px] ${
                l.featured ? 'text-[#0068B3] font-medium' : 'text-[#444]'
              }`}
            >
              <span className="flex items-center gap-2.5">
                <span
                  className={`flex-shrink-0 rounded-full transition-all duration-150 ${
                    l.featured
                      ? 'w-1.5 h-1.5 bg-[#0096FF]'
                      : 'w-1 h-1 bg-gray-300 group-hover:bg-[#0096FF] group-hover:w-1.5 group-hover:h-1.5'
                  }`}
                />
                {l.label}
              </span>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[#0068B3] text-[11px] ml-2">→</span>
            </Link>
          ))}
        </div>
        <div className="mt-3 pt-2.5" style={{ borderTop: '0.5px solid #EAEAEA' }}>
          <Link
            to="/visas"
            className="inline-flex items-center gap-1.5 font-display font-semibold text-[11.5px] uppercase tracking-[0.6px] text-[#0068B3] hover:gap-2.5 transition-all"
          >
            View all visa services →
          </Link>
        </div>
      </div>

      {/* Right: WhatsApp card */}
      <div className="w-56 shrink-0">
        <WaCard />
      </div>
    </div>
  );
}

// --- Corporate Travel Mega Panel ----------------------------------------------
function CorporateMegaPanel({ active }: { active: string }) {
  return (
    <div className="flex gap-8">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-4 pb-3" style={{ borderBottom: '0.5px solid #EAEAEA' }}>
          <div className="w-8 h-8 rounded-[8px] flex items-center justify-center shrink-0" style={{ background: '#E8F4FD' }}>
            <Briefcase className="w-4 h-4" style={{ color: '#0068B3', strokeWidth: 2 }} />
          </div>
          <div>
            <p className="font-display font-semibold text-[13px] text-[#071126] leading-tight">Corporate Travel Management</p>
            <p className="font-sans text-[11px] text-[#999] mt-0.5">Industry travel programmes from our Mussafah, Abu Dhabi office</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-0.5">
          {CORPORATE_NAV.map(l => {
            const isOn = active === l.path || active === l.path + '/';
            return (
              <Link
                key={l.path}
                to={l.path}
                className={`group flex items-center justify-between py-2.5 border-b border-[#F5F5F5] text-[13px] font-sans transition-all duration-150 hover:text-[#0068B3] hover:translate-x-[2px] ${
                  isOn ? 'text-[#0068B3] font-medium' : 'text-[#444]'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <span className={`flex-shrink-0 rounded-full transition-all duration-150 ${
                    isOn ? 'w-1.5 h-1.5 bg-[#0096FF]' : 'w-1 h-1 bg-gray-300 group-hover:bg-[#0096FF] group-hover:w-1.5 group-hover:h-1.5'
                  }`} />
                  {l.name}
                </span>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[#0068B3] text-[11px] ml-2">→</span>
              </Link>
            );
          })}
        </div>
        <div className="mt-3 pt-2.5" style={{ borderTop: '0.5px solid #EAEAEA' }}>
          <Link
            to="/corporate-travel"
            className="inline-flex items-center gap-1.5 font-display font-semibold text-[11.5px] uppercase tracking-[0.6px] text-[#0068B3] hover:gap-2.5 transition-all"
          >
            Corporate travel overview →
          </Link>
        </div>
      </div>

      <div className="w-56 shrink-0">
        <WaCard />
      </div>
    </div>
  );
}

// --- WhatsApp Card -------------------------------------------------------------
function WaCard() {
  return (
    <div
      className="rounded-xl p-4 relative overflow-hidden flex flex-col gap-3 h-full"
      style={{ background: 'linear-gradient(140deg, #071126 0%, #15355A 100%)' }}
    >
      <div
        className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-3xl pointer-events-none"
        style={{ background: '#0096FF', opacity: 0.12 }}
      />
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </span>
        <span className="text-[9.5px] font-display font-semibold uppercase tracking-[0.25em] text-green-400">
          Available Now
        </span>
      </div>
      <div>
        <p className="font-display font-black text-white text-[14px] leading-snug mb-1">
          Need visa help?
        </p>
        <p className="text-[11.5px] font-medium leading-relaxed" style={{ color: '#A8D8F0' }}>
          Chat with our UAE-based visa team - reply within minutes.
        </p>
      </div>
      <a
        href={`${WA_URL}?text=Hi%20Big%20Bird%20Travel%2C%20I%20need%20help%20with%20a%20visa%20application.`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-auto flex items-center justify-center gap-2 font-display font-semibold text-white text-[12px] uppercase tracking-[0.2em] py-2.5 rounded-lg transition-all hover:opacity-90 active:scale-[0.98]"
        style={{ background: '#0F7A35' }}
      >
        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Us
      </a>
    </div>
  );
}

// --- Nav Item helpers ----------------------------------------------------------
function NavItem({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className={`relative px-[7px] py-[10px] font-display text-[12px] font-medium uppercase tracking-[0.2px] transition-colors group whitespace-nowrap ${
        active ? 'text-[#0068B3]' : 'text-[#071126] hover:text-[#0068B3]'
      }`}
    >
      {children}
      <span
        className={`absolute bottom-0 left-2 right-2 h-[2px] rounded-full bg-[#0096FF] transition-transform duration-200 origin-left ${
          active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
        }`}
      />
    </Link>
  );
}

function NavTrigger({
  active, isMenuOpen, children, onMouseEnter, onMouseLeave, onClick,
}: {
  active: boolean; isMenuOpen: boolean; children: React.ReactNode;
  onMouseEnter: () => void; onMouseLeave: () => void; onClick: () => void;
}) {
  return (
    <button
      className={`relative flex items-center gap-1 px-[7px] py-[10px] font-display text-[12px] font-medium uppercase tracking-[0.2px] transition-colors group whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0096FF] rounded-sm ${
        active || isMenuOpen ? 'text-[#0068B3]' : 'text-[#071126] hover:text-[#0068B3]'
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      aria-haspopup="true"
      aria-expanded={isMenuOpen}
    >
      {children}
      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
      <span
        className={`absolute bottom-0 left-2 right-2 h-[2px] rounded-full bg-[#0096FF] transition-transform duration-200 origin-left ${
          active || isMenuOpen ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
        }`}
      />
    </button>
  );
}

// --- Mobile Helpers ------------------------------------------------------------
function MobLink({ to, children, onClose }: { to: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="flex items-center px-3 py-3 font-display font-medium text-[13px] text-[#071126] uppercase tracking-[0.3px] hover:text-[#0068B3] hover:bg-slate-50 rounded-lg transition-colors"
    >
      {children}
    </Link>
  );
}
